if (!customElements.get('as-seen-on-media')) {
  class AsSeenOnMedia extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.playBtn = this.querySelector('.as-seen-on__play-btn');

      if (!this.video) return;

      if (this.dataset.autoplay === 'true') {
        this.playBtn?.classList.add('is-hidden');
        return;
      }

      this.playBtn?.addEventListener('click', () => this.togglePlay());
      this.video.addEventListener('click', () => this.togglePlay());
      this.video.addEventListener('play', () => this.classList.add('is-playing'));
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
      this.video.addEventListener('ended', () => {
        this.classList.remove('is-playing');
        if (this.dataset.loop !== 'true') {
          this.playBtn?.classList.remove('is-hidden');
        }
      });
    }

    togglePlay() {
      if (!this.video) return;

      if (this.video.paused) {
        this.video.play();
        this.playBtn?.classList.add('is-hidden');
      } else {
        this.video.pause();
        this.playBtn?.classList.remove('is-hidden');
      }
    }
  }

  customElements.define('as-seen-on-media', AsSeenOnMedia);
}

if (!customElements.get('as-seen-on-slider')) {
  class AsSeenOnSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.as-seen-on__track');
      this.progressBar = this.querySelector('.as-seen-on__progress-bar');
      this.slides = this.track ? [...this.track.querySelectorAll('.as-seen-on__item')] : [];

      if (!this.track || this.slides.length <= 1) {
        this.updateProgress();
        return;
      }

      this.autoplayEnabled = this.dataset.autoplaySlider !== 'false';
      this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 4) * 1000;
      this.resumeDelay = 5000;
      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.isPaused = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = () => this.updateProgress();
      this.onPointerDown = () => this.pauseAutoplay();
      this.onPointerUp = () => this.scheduleResume();
      this.onTouchEnd = () => this.scheduleResume();
      this.onMouseEnter = () => this.pauseAutoplay();
      this.onMouseLeave = () => {
        if (this.autoplayEnabled && !this.reducedMotion) this.startAutoplay();
      };
      this.onResize = () => this.updateProgress();

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.track.addEventListener('mouseenter', this.onMouseEnter);
      this.track.addEventListener('mouseleave', this.onMouseLeave);
      window.addEventListener('resize', this.onResize);

      this.updateProgress();

      if (this.autoplayEnabled && !this.reducedMotion) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);

      if (!this.track) return;

      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('touchend', this.onTouchEnd);
      this.track.removeEventListener('mouseenter', this.onMouseEnter);
      this.track.removeEventListener('mouseleave', this.onMouseLeave);
      window.removeEventListener('resize', this.onResize);
    }

    getSlideStep() {
      const slide = this.slides[0];
      if (!slide) return 0;

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return slide.offsetWidth + gap;
    }

    getMaxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    scrollToNext() {
      const step = this.getSlideStep();
      const maxScroll = this.getMaxScroll();

      if (step <= 0 || maxScroll <= 0) return;

      const nextPosition = this.track.scrollLeft + step;

      if (nextPosition >= maxScroll - 2) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.track.scrollTo({ left: nextPosition, behavior: 'smooth' });
      }
    }

    startAutoplay() {
      if (this.isPaused || !this.autoplayEnabled || this.reducedMotion) return;

      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => this.scrollToNext(), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    pauseAutoplay() {
      this.isPaused = true;
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);

      this.resumeTimer = window.setTimeout(() => {
        this.isPaused = false;
        if (this.autoplayEnabled && !this.reducedMotion) {
          this.startAutoplay();
        }
      }, this.resumeDelay);
    }

    updateProgress() {
      if (!this.progressBar || !this.track) return;

      const maxScroll = this.getMaxScroll();
      const progress = maxScroll > 0 ? (this.track.scrollLeft / maxScroll) * 100 : 100;
      this.progressBar.style.width = `${progress}%`;
    }
  }

  customElements.define('as-seen-on-slider', AsSeenOnSlider);
}
